import { TableRow } from '@features/powerpoint';
import { TopData } from './top-data-from-report';
import {
  headersToTableRow,
  cellOptionsByIndex,
} from '../top-states-dmas-locations/table-utils';
import { postalCodeToStateName } from '@utils/states';
import { formatters } from '@utils/formattersV2';
import { capitalize } from '@utils/strings';
import { MISSING_VALUE } from '@constants/stats';

const headerRow = headersToTableRow(['Category', '', 'Index/%'], {
  fontSize: 10,
});

type Row = {
  category: string;
  name: string | undefined;
  value: number | string | undefined;
};

const tableRow = (
  category: string,
  name: string | undefined,
  value: number | string | undefined
) =>
  [
    {
      text: category,
      options: { bold: true },
    },
    {
      text: name ?? MISSING_VALUE,
      options: { align: 'center' },
    },
    {
      text: value ? value.toString() : MISSING_VALUE,
      options: { align: 'center' },
    },
  ] as const satisfies TableRow;

export const tableRows = (topData: TopData): TableRow[] => {
  const {
    topGenderData,
    topIncomeData,
    topStateData,
    topAgeData,
    topDmaData,
    topProximityData,
    topTimeToFirstVisitData,
    topVisitsByTimeOfDayData,
    topVisitsByDayOfWeekData,
  } = topData;

  const rows: Row[] = [
    {
      category: 'Gender',
      name: topGenderData?.name,
      value: topGenderData?.value,
    },
    {
      category: 'Income',
      name: topIncomeData?.name,
      value: topIncomeData?.value,
    },
    {
      category: 'Age Range',
      name: topAgeData?.name,
      value: topAgeData?.value,
    },
    {
      category: 'State',
      name:
        postalCodeToStateName[
          topStateData?.location as keyof typeof postalCodeToStateName
        ] ?? topStateData?.location,
      value: topStateData?.visitIndex,
    },
    {
      category: 'DMA',
      name: topDmaData?.location,
      value: topDmaData?.visitIndex,
    },
    {
      category: 'Proximity to Home',
      name: topProximityData?.name,
      value: formatters.percentage(topProximityData?.value),
    },
    {
      category: 'Time to First Visit',
      name: topTimeToFirstVisitData
        ? `${topTimeToFirstVisitData.daysAfterAdExposure} day${topTimeToFirstVisitData.daysAfterAdExposure === 1 ? '' : 's'}`
        : MISSING_VALUE,
      value: formatters.percentage(topTimeToFirstVisitData?.percentageOfUsers),
    },
    {
      category: 'Visits by Time of Day',
      name: topVisitsByTimeOfDayData?.name,
      value: formatters.number(topVisitsByTimeOfDayData?.y),
    },
    {
      category: 'Visits by Day of Week',
      name: capitalize(topVisitsByDayOfWeekData?.name ?? MISSING_VALUE),
      value: topVisitsByDayOfWeekData?.value,
    },
  ];

  const baseDataRows = rows.map(row =>
    tableRow(row.category, row.name, row.value)
  );

  const dataRows = baseDataRows.map((row, rowIndex) =>
    row.map(cell => ({
      ...cell,
      options: {
        ...cell.options,
        ...cellOptionsByIndex(rowIndex, baseDataRows.length),
      },
    }))
  );

  return [headerRow, ...dataRows];
};
